"use client";

import {
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	hasMediaElementAnalyser,
	setMediaElementPlaybackVolume,
} from "../use-media-element-waveform";
import { useStaggeredRevealPercent } from "../use-smooth-media-values";
import { resolveBufferedEndPercent } from "../waveform-bar-visual";
import {
	buildWaveformLevelTimeline,
	decodeAudioBufferFromUrl,
	type WaveformLevelTimeline,
} from "../waveform-level-timeline";
import {
	DEFAULT_BUFFER_AHEAD_MS,
	isBufferedAheadOfPosition,
	resolveSegmentDurationMsFromAudio,
} from "./buffer-ahead";
import {
	INTERACTIVE_AUDIO_MIN_REVEAL_DURATION_MS,
	INTERACTIVE_AUDIO_WAVEFORM_BAR_COUNT,
} from "./constants";
import {
	isAudioAtEndOfTrack,
	isReplayPlayingFromStart,
	isReplaySeekPending,
	prepareReplaySeek,
	resolveAudioProgressPercent,
} from "./interactive-audio-playback";
import {
	resolveInteractiveAudioBufferThresholdPercent,
	waitForAudioBufferThreshold,
	waitForAudioDuration,
	waitForRevealDisplayCatchUp,
} from "./interactive-audio-prepare";
import { useInteractiveAudioBufferAhead } from "./use-interactive-audio-buffer-ahead";

export type InteractiveAudioTransportState =
	| "idle"
	| "loading"
	| "playing"
	| "paused";

export interface UseInteractiveAudioTransportOptions {
	sessionId: string;
	volume: number;
	showWaveform: boolean;
	activeSessionId: string | null;
	onRequestSample: () => Promise<{ src: string; mimeType?: string } | null>;
	onActivateSession: (sessionId: string) => void;
	onRegisterStop: (sessionId: string, stop: () => void) => void;
	onUnregisterStop: (sessionId: string) => void;
}

export interface UseInteractiveAudioTransportResult {
	audioRef: RefObject<HTMLAudioElement | null>;
	waveformTimeline: WaveformLevelTimeline | null;
	transportState: InteractiveAudioTransportState;
	displayProgressPercent: number;
	bufferedPercent: number;
	displayRevealPercent: number;
	bufferRevealSessionKey: number;
	bufferRevealSessionStartMs: number;
	isPreparingPlayback: boolean;
	isAtTrackEnd: boolean;
	isSessionActive: boolean;
	togglePlayback: () => Promise<void>;
	replay: () => Promise<void>;
	pauseForScrub: () => void;
	seekToPercent: (percent: number) => void;
	stopPlayback: () => void;
}

function resolveTransportState(
	audio: HTMLAudioElement,
	loading: boolean,
	active: boolean,
): InteractiveAudioTransportState {
	if (!active) {
		return "idle";
	}
	if (loading) {
		return "loading";
	}
	return audio.paused ? "paused" : "playing";
}

export function useInteractiveAudioTransport({
	sessionId,
	volume,
	showWaveform,
	activeSessionId,
	onRequestSample,
	onActivateSession,
	onRegisterStop,
	onUnregisterStop,
}: UseInteractiveAudioTransportOptions): UseInteractiveAudioTransportResult {
	const audioRef = useRef<HTMLAudioElement>(null);
	const objectUrlRef = useRef<string | null>(null);
	const loadedSrcRef = useRef<string | null>(null);
	const timelineRequestRef = useRef(0);
	const timelineReadyRef = useRef<Promise<void> | null>(null);
	const isLoadingRef = useRef(false);
	const optimisticProgressRef = useRef<number | null>(null);
	const replayInProgressRef = useRef(false);
	const cancelReplaySeekRef = useRef<(() => void) | null>(null);
	const playbackGenerationRef = useRef(0);
	const bufferedPercentRef = useRef(0);
	const displayRevealRef = useRef(0);
	const revealSessionStartMsRef = useRef(0);

	const [transportState, setTransportState] =
		useState<InteractiveAudioTransportState>("idle");
	const [displayProgressPercent, setDisplayProgressPercent] = useState(0);
	const [bufferedPercent, setBufferedPercent] = useState(0);
	const [isAtTrackEnd, setIsAtTrackEnd] = useState(false);
	const [isPreparingPlayback, setIsPreparingPlayback] = useState(false);
	const [waveformTimeline, setWaveformTimeline] =
		useState<WaveformLevelTimeline | null>(null);
	const [bufferRevealSessionKey, setBufferRevealSessionKey] = useState(0);
	const [bufferRevealSessionStartMs, setBufferRevealSessionStartMs] =
		useState(0);

	const activeSessionIdRef = useRef(activeSessionId);
	activeSessionIdRef.current = activeSessionId;
	/** Optimistic active session — React context can lag one frame behind play gestures. */
	const pendingActiveSessionIdRef = useRef<string | null>(null);

	const resolveIsSessionActive = useCallback(() => {
		if (activeSessionIdRef.current !== sessionId) {
			return false;
		}
		return (
			transportState === "playing" ||
			transportState === "paused" ||
			transportState === "loading" ||
			isPreparingPlayback ||
			isLoadingRef.current ||
			loadedSrcRef.current != null
		);
	}, [isPreparingPlayback, sessionId, transportState]);

	const isSessionActive = resolveIsSessionActive();

	const handleBufferRevealSweepStart = useCallback((startMs: number) => {
		revealSessionStartMsRef.current = startMs;
		setBufferRevealSessionStartMs(startMs);
	}, []);

	const readPositionMs = useCallback(() => {
		const audio = audioRef.current;
		if (!audio || !Number.isFinite(audio.currentTime)) {
			return 0;
		}
		return Math.max(0, audio.currentTime * 1000);
	}, []);

	const bufferAhead = useInteractiveAudioBufferAhead({
		audioRef,
		getPositionMs: readPositionMs,
		getShouldMonitorPlayback: () =>
			activeSessionId === sessionId &&
			(transportState === "playing" || isLoadingRef.current),
	});

	const isBufferRevealEnabled =
		(pendingActiveSessionIdRef.current ?? activeSessionIdRef.current) ===
			sessionId &&
		(isPreparingPlayback ||
			transportState === "loading" ||
			bufferAhead.isBuffering);

	const displayRevealPercent = useStaggeredRevealPercent(
		bufferedPercent,
		isBufferRevealEnabled,
		bufferRevealSessionKey,
		{
			minTotalDurationMs: INTERACTIVE_AUDIO_MIN_REVEAL_DURATION_MS,
			onSweepStart: handleBufferRevealSweepStart,
		},
	);

	useEffect(() => {
		displayRevealRef.current = displayRevealPercent;
	}, [displayRevealPercent]);

	const syncProgressFromAudio = useCallback((audio: HTMLAudioElement) => {
		if (replayInProgressRef.current) {
			if (isReplayPlayingFromStart(audio)) {
				replayInProgressRef.current = false;
			} else {
				return resolveAudioProgressPercent(audio);
			}
		}

		const actual = resolveAudioProgressPercent(audio);
		const optimistic = optimisticProgressRef.current;
		const atEnd = isAudioAtEndOfTrack(audio);

		if (optimistic != null) {
			if (Math.abs(actual - optimistic) < 0.5) {
				optimisticProgressRef.current = null;
			} else {
				setDisplayProgressPercent(optimistic);
				setIsAtTrackEnd(optimistic >= 99.5);
				return actual;
			}
		}

		setDisplayProgressPercent(actual);
		setIsAtTrackEnd(atEnd);

		return actual;
	}, []);

	const syncBufferFromAudio = useCallback((audio: HTMLAudioElement) => {
		const next = resolveBufferedEndPercent(audio);
		bufferedPercentRef.current = next;
		setBufferedPercent(next);
	}, []);

	const commitBufferedPercent = useCallback((next: number) => {
		bufferedPercentRef.current = next;
		setBufferedPercent(next);
	}, []);

	const cancelReplay = useCallback(() => {
		replayInProgressRef.current = false;
		cancelReplaySeekRef.current?.();
		cancelReplaySeekRef.current = null;
	}, []);

	const loadWaveformTimeline = useCallback(
		async (src: string): Promise<WaveformLevelTimeline | null> => {
			if (!showWaveform) {
				setWaveformTimeline(null);
				return null;
			}

			const requestId = timelineRequestRef.current + 1;
			timelineRequestRef.current = requestId;

			const buffer = await decodeAudioBufferFromUrl(src);
			if (timelineRequestRef.current !== requestId) {
				return null;
			}

			if (!buffer) {
				setWaveformTimeline(null);
				return null;
			}

			const timeline = await buildWaveformLevelTimeline(
				buffer,
				INTERACTIVE_AUDIO_WAVEFORM_BAR_COUNT,
			);
			setWaveformTimeline(timeline);
			return timeline;
		},
		[showWaveform],
	);

	const startTimelineLoad = useCallback(
		(src: string) => {
			const timelinePromise = loadWaveformTimeline(src).then(
				() => undefined,
			);
			timelineReadyRef.current = timelinePromise;
			return timelinePromise;
		},
		[loadWaveformTimeline],
	);

	const wireOutput = useCallback(
		(audio: HTMLAudioElement) => {
			setMediaElementPlaybackVolume(audio, volume);
		},
		[volume],
	);

	const replaceAudioElementIfWebAudioHijacked = useCallback(() => {
		const audio = audioRef.current;
		if (!audio?.parentNode || !hasMediaElementAnalyser(audio)) {
			return;
		}
		const replacement = audio.cloneNode(false) as HTMLAudioElement;
		audio.parentNode.replaceChild(replacement, audio);
		audioRef.current = replacement;
	}, []);

	const clearAudioElement = useCallback(() => {
		const audio = audioRef.current;
		if (audio) {
			audio.pause();
			audio.removeAttribute("src");
			audio.load();
		}
		replaceAudioElementIfWebAudioHijacked();
	}, [replaceAudioElementIfWebAudioHijacked]);

	const assignSampleSrc = useCallback(
		(src: string) => {
			const audio = audioRef.current;
			if (!audio) {
				return;
			}

			audio.src = src;
			loadedSrcRef.current = src;
			startTimelineLoad(src);
		},
		[startTimelineLoad],
	);

	const stopPlayback = useCallback(() => {
		playbackGenerationRef.current += 1;
		const audio = audioRef.current;
		cancelReplay();

		if (audio) {
			audio.pause();
			audio.currentTime = 0;
		}

		timelineRequestRef.current += 1;
		timelineReadyRef.current = null;
		optimisticProgressRef.current = null;
		isLoadingRef.current = false;
		setIsPreparingPlayback(false);
		loadedSrcRef.current = null;
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}
		setWaveformTimeline(null);
		setDisplayProgressPercent(0);
		commitBufferedPercent(0);
		setIsAtTrackEnd(false);
		setTransportState("idle");
		if (pendingActiveSessionIdRef.current === sessionId) {
			pendingActiveSessionIdRef.current = null;
		}
	}, [cancelReplay, commitBufferedPercent, sessionId]);

	const resetAudioElement = useCallback(() => {
		clearAudioElement();
		timelineRequestRef.current += 1;
		timelineReadyRef.current = null;
		loadedSrcRef.current = null;
		setWaveformTimeline(null);
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}
	}, [clearAudioElement]);

	const beginBufferRevealSession = useCallback(() => {
		setBufferRevealSessionKey((current) => current + 1);
		displayRevealRef.current = 0;
		commitBufferedPercent(0);
		setIsPreparingPlayback(true);
	}, [commitBufferedPercent]);

	const waitForPlaybackPreparation = useCallback(async () => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		await waitForAudioDuration(audio);
		const thresholdPercent =
			resolveInteractiveAudioBufferThresholdPercent(audio);
		// Timeline decode is for live playback bars only — never gate audible play.
		void (timelineReadyRef.current ?? Promise.resolve());
		await waitForAudioBufferThreshold(audio, thresholdPercent);

		if (showWaveform) {
			await waitForRevealDisplayCatchUp(
				() => displayRevealRef.current,
				() => bufferedPercentRef.current,
				INTERACTIVE_AUDIO_MIN_REVEAL_DURATION_MS,
				() => revealSessionStartMsRef.current,
				{ barCount: INTERACTIVE_AUDIO_WAVEFORM_BAR_COUNT },
			);
		}
	}, [showWaveform]);

	const isCurrentPlaybackGeneration = useCallback(
		(generation: number) => generation === playbackGenerationRef.current,
		[],
	);

	const prepareSamplePlayback = useCallback(async () => {
		const audio = audioRef.current;
		if (!audio) {
			return false;
		}
		if (loadedSrcRef.current) {
			return true;
		}

		const playbackGeneration = playbackGenerationRef.current;
		isLoadingRef.current = true;
		setIsPreparingPlayback(true);
		setTransportState("loading");

		const sample = await onRequestSample();
		if (!sample) {
			isLoadingRef.current = false;
			setIsPreparingPlayback(false);
			setTransportState("idle");
			return false;
		}

		clearAudioElement();
		loadedSrcRef.current = null;
		setWaveformTimeline(null);
		timelineReadyRef.current = null;
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}

		const mimeType =
			sample.mimeType?.startsWith("audio/") === true
				? sample.mimeType
				: "audio/mpeg";

		if (sample.src.startsWith("blob:") || sample.src.startsWith("http")) {
			assignSampleSrc(sample.src);
		} else {
			const blob = new Blob(
				[Uint8Array.from(atob(sample.src), (c) => c.charCodeAt(0))],
				{ type: mimeType },
			);
			const url = URL.createObjectURL(blob);
			objectUrlRef.current = url;
			assignSampleSrc(url);
		}

		wireOutput(audio);
		syncBufferFromAudio(audio);

		await waitForPlaybackPreparation();

		if (!isCurrentPlaybackGeneration(playbackGeneration)) {
			return false;
		}

		isLoadingRef.current = false;
		setIsPreparingPlayback(false);
		optimisticProgressRef.current = null;
		setDisplayProgressPercent(0);
		setIsAtTrackEnd(false);
		setTransportState("paused");
		return true;
	}, [
		assignSampleSrc,
		clearAudioElement,
		isCurrentPlaybackGeneration,
		onRequestSample,
		syncBufferFromAudio,
		waitForPlaybackPreparation,
		wireOutput,
	]);

	const isPositionBufferedNow = useCallback(() => {
		const audio = audioRef.current;
		if (!audio?.src) {
			return false;
		}
		const positionMs = readPositionMs();
		const segmentDurationMs = resolveSegmentDurationMsFromAudio(audio);
		return isBufferedAheadOfPosition(audio, positionMs, segmentDurationMs, {
			minBufferAheadMs: DEFAULT_BUFFER_AHEAD_MS,
		});
	}, [readPositionMs]);

	const syncTransportFromAudio = useCallback(
		(audio: HTMLAudioElement, loadingOverride?: boolean) => {
			const effectiveActiveSessionId =
				pendingActiveSessionIdRef.current ?? activeSessionIdRef.current;
			const showLoading =
				(loadingOverride ?? isLoadingRef.current) &&
				audio.paused &&
				!audio.ended;
			setTransportState(
				resolveTransportState(
					audio,
					showLoading,
					effectiveActiveSessionId === sessionId,
				),
			);
		},
		[sessionId],
	);

	const startPlayback = useCallback(
		async (
			requestedAudio: HTMLAudioElement,
			options: { skipBufferWait?: boolean } = {},
		) => {
			let audio = requestedAudio;
			if (hasMediaElementAnalyser(audio) && loadedSrcRef.current) {
				const src = loadedSrcRef.current;
				replaceAudioElementIfWebAudioHijacked();
				const replacement = audioRef.current;
				if (replacement) {
					replacement.src = src;
					audio = replacement;
				}
			}
			cancelReplay();
			wireOutput(audio);
			if (
				!options.skipBufferWait &&
				!isPositionBufferedNow() &&
				!bufferAhead.isBufferedForCurrentPosition
			) {
				isLoadingRef.current = true;
				setTransportState("loading");
				await bufferAhead.waitForCurrentPositionBuffer();
				isLoadingRef.current = false;
			}
			try {
				const effectiveActiveSessionId =
					pendingActiveSessionIdRef.current ??
					activeSessionIdRef.current;
				if (effectiveActiveSessionId !== sessionId) {
					return;
				}
				await audio.play();
				isLoadingRef.current = false;
				syncTransportFromAudio(audio, false);
			} catch {
				isLoadingRef.current = false;
				syncProgressFromAudio(audio);
				syncTransportFromAudio(audio, false);
			}
		},
		[
			bufferAhead,
			cancelReplay,
			isPositionBufferedNow,
			replaceAudioElementIfWebAudioHijacked,
			sessionId,
			syncProgressFromAudio,
			syncTransportFromAudio,
			wireOutput,
		],
	);

	const playReplayFromStart = useCallback(
		async (audio: HTMLAudioElement) => {
			if (!replayInProgressRef.current) {
				return;
			}

			if (isReplaySeekPending(audio)) {
				return;
			}

			try {
				await audio.play();
			} catch {
				if (!replayInProgressRef.current) {
					return;
				}
				replayInProgressRef.current = false;
				syncProgressFromAudio(audio);
				setTransportState("paused");
			}
		},
		[syncProgressFromAudio],
	);

	const beginReplay = useCallback(
		(audio: HTMLAudioElement) => {
			cancelReplaySeekRef.current?.();
			replayInProgressRef.current = true;
			setDisplayProgressPercent(0);
			setIsAtTrackEnd(false);

			wireOutput(audio);

			cancelReplaySeekRef.current = prepareReplaySeek(audio, () => {
				void playReplayFromStart(audio);
			});
		},
		[playReplayFromStart, wireOutput],
	);

	const replay = useCallback(async () => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		onActivateSession(sessionId);
		pendingActiveSessionIdRef.current = sessionId;

		if (loadedSrcRef.current) {
			beginReplay(audio);
			return;
		}

		replayInProgressRef.current = true;
		setDisplayProgressPercent(0);
		setIsAtTrackEnd(false);
		beginBufferRevealSession();

		const ready = await prepareSamplePlayback();
		if (!ready) {
			replayInProgressRef.current = false;
			syncProgressFromAudio(audio);
			return;
		}

		beginReplay(audio);
	}, [
		onActivateSession,
		beginBufferRevealSession,
		beginReplay,
		prepareSamplePlayback,
		syncProgressFromAudio,
		sessionId,
	]);

	const togglePlayback = useCallback(async () => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		onActivateSession(sessionId);
		pendingActiveSessionIdRef.current = sessionId;

		const wasFirstLoad = !loadedSrcRef.current;
		if (wasFirstLoad) {
			beginBufferRevealSession();
			const ready = await prepareSamplePlayback();
			if (!ready) {
				return;
			}
		}

		const effectiveActiveSessionId =
			pendingActiveSessionIdRef.current ?? activeSessionIdRef.current;
		if (effectiveActiveSessionId !== sessionId) {
			return;
		}

		if (!audio.paused) {
			audio.pause();
			isLoadingRef.current = false;
			setTransportState("paused");
			return;
		}

		await startPlayback(audio, { skipBufferWait: wasFirstLoad });
	}, [
		onActivateSession,
		beginBufferRevealSession,
		prepareSamplePlayback,
		startPlayback,
		sessionId,
	]);

	const wasPlayingBeforeScrubRef = useRef(false);

	const pauseForScrub = useCallback(() => {
		const audio = audioRef.current;
		wasPlayingBeforeScrubRef.current = audio != null && !audio.paused;
		if (audio && !audio.paused) {
			audio.pause();
		}
	}, []);

	const seekToPercent = useCallback(
		(percent: number) => {
			const audio = audioRef.current;
			if (
				!audio ||
				!Number.isFinite(audio.duration) ||
				audio.duration <= 0
			) {
				return;
			}

			cancelReplay();

			const clamped = Math.max(0, Math.min(100, percent));
			optimisticProgressRef.current = clamped;
			setDisplayProgressPercent(clamped);
			setIsAtTrackEnd(clamped >= 99.5);

			try {
				const nextMs = (clamped / 100) * audio.duration * 1000;
				audio.currentTime = (clamped / 100) * audio.duration;
				const restorePlaybackIntent = wasPlayingBeforeScrubRef.current;
				wasPlayingBeforeScrubRef.current = false;
				void bufferAhead.ensureBufferAtPosition(nextMs, {
					blocking: true,
					restorePlaybackIntent,
				});
			} catch {
				optimisticProgressRef.current = null;
				syncProgressFromAudio(audio);
			}
		},
		[bufferAhead, cancelReplay, syncProgressFromAudio],
	);

	useEffect(() => {
		setMediaElementPlaybackVolume(audioRef.current, volume);
	}, [volume]);

	useEffect(() => {
		onRegisterStop(sessionId, stopPlayback);
		return () => {
			onUnregisterStop(sessionId);
			stopPlayback();
			resetAudioElement();
		};
	}, [
		sessionId,
		onRegisterStop,
		onUnregisterStop,
		stopPlayback,
		resetAudioElement,
	]);

	useEffect(() => {
		if (activeSessionId === sessionId) {
			pendingActiveSessionIdRef.current = sessionId;
			return;
		}
		pendingActiveSessionIdRef.current = null;
		if (
			transportState !== "idle" ||
			isPreparingPlayback ||
			isLoadingRef.current ||
			loadedSrcRef.current != null
		) {
			stopPlayback();
		}
	}, [
		activeSessionId,
		isPreparingPlayback,
		sessionId,
		stopPlayback,
		transportState,
	]);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		const syncTransport = () => {
			syncTransportFromAudio(audio);
		};

		const handleTimeUpdate = () => {
			syncProgressFromAudio(audio);
			syncBufferFromAudio(audio);
			bufferAhead.checkPlaybackBufferEdge();
		};

		const handleSeeked = () => {
			syncProgressFromAudio(audio);
		};

		const handleProgress = () => {
			syncBufferFromAudio(audio);
		};

		const handleLoadedData = () => {
			syncBufferFromAudio(audio);
		};

		const handleCanPlay = () => {
			syncBufferFromAudio(audio);
		};

		const handlePlay = () => {
			syncTransport();
			if (
				replayInProgressRef.current &&
				isReplayPlayingFromStart(audio)
			) {
				replayInProgressRef.current = false;
			}
		};

		const handlePause = () => {
			syncTransport();
			if (!replayInProgressRef.current) {
				syncProgressFromAudio(audio);
			}
		};

		const handleEnded = () => {
			if (replayInProgressRef.current) {
				cancelReplaySeekRef.current?.();
				cancelReplaySeekRef.current = prepareReplaySeek(audio, () => {
					void playReplayFromStart(audio);
				});
				return;
			}

			syncProgressFromAudio(audio);
			const effectiveActiveSessionId =
				pendingActiveSessionIdRef.current ?? activeSessionIdRef.current;
			setTransportState(
				effectiveActiveSessionId === sessionId && !isLoadingRef.current
					? "paused"
					: "idle",
			);
		};

		const handleError = () => {
			cancelReplay();
			resetAudioElement();
			setIsPreparingPlayback(false);
			setTransportState("idle");
			setDisplayProgressPercent(0);
			commitBufferedPercent(0);
			setIsAtTrackEnd(false);
		};

		audio.addEventListener("play", handlePlay);
		audio.addEventListener("pause", handlePause);
		audio.addEventListener("ended", handleEnded);
		audio.addEventListener("timeupdate", handleTimeUpdate);
		audio.addEventListener("seeked", handleSeeked);
		audio.addEventListener("progress", handleProgress);
		audio.addEventListener("loadeddata", handleLoadedData);
		audio.addEventListener("canplay", handleCanPlay);
		audio.addEventListener("error", handleError);

		return () => {
			audio.removeEventListener("play", handlePlay);
			audio.removeEventListener("pause", handlePause);
			audio.removeEventListener("ended", handleEnded);
			audio.removeEventListener("timeupdate", handleTimeUpdate);
			audio.removeEventListener("seeked", handleSeeked);
			audio.removeEventListener("progress", handleProgress);
			audio.removeEventListener("loadeddata", handleLoadedData);
			audio.removeEventListener("canplay", handleCanPlay);
			audio.removeEventListener("error", handleError);
		};
	}, [
		activeSessionId,
		bufferAhead,
		cancelReplay,
		playReplayFromStart,
		resetAudioElement,
		syncBufferFromAudio,
		syncProgressFromAudio,
		syncTransportFromAudio,
		sessionId,
		commitBufferedPercent,
	]);

	useEffect(() => {
		bufferAhead.registerResumePlaybackHandler(() => {
			const audio = audioRef.current;
			if (audio) {
				void startPlayback(audio, {
					skipBufferWait: loadedSrcRef.current != null,
				});
			}
		});
		return () => {
			bufferAhead.registerResumePlaybackHandler(null);
		};
	}, [bufferAhead, startPlayback]);

	return {
		audioRef,
		waveformTimeline,
		transportState,
		displayProgressPercent,
		bufferedPercent,
		displayRevealPercent,
		bufferRevealSessionKey,
		bufferRevealSessionStartMs,
		isPreparingPlayback,
		isAtTrackEnd,
		isSessionActive,
		togglePlayback,
		replay,
		pauseForScrub,
		seekToPercent,
		stopPlayback,
	};
}
